/**
 * Curated course discovery, NOT a training completion or accreditation engine.
 *
 * Verified public course/product pages (23 October 2026), kept as direct links
 * rather than fabricated checkout deep-links. Employers still check certificate
 * scope, learning outcomes, level, date and any in-person practical assessment.
 * Never silently turn choosing a course into verified evidence.
 */
export const HSG_CATALOGUE_URL = 'https://www.healthandsafetygroup.com/healthcare/elearning';
export const HSG_PRACTICAL_URL = 'https://www.healthandsafetygroup.com/p/1963';
export const HSG_FULL_DAY_URL = 'https://www.healthandsafetygroup.com/p/1484';
export const HOME_OFFICE_PREVENT_URL = 'https://www.gov.uk/guidance/prevent-duty-training';

const HSG = 'The Health & Safety Group';
const course = (slug, title, extra = {}) => ({
  url: `https://www.healthandsafetygroup.com/healthcare/elearning/${slug}`,
  title, provider: HSG, price: 'Check course price', ...extra,
});

// This does not certify an employee or make an e-learning module a regulated
// qualification. It is evidence of course-level third-party quality assurance,
// checked against HSG's actual course page on 25 October 2026.
const verifiedFreeCstf = (slug, title) => course(slug, title, {
  price: 'Free',
  qualityAssurance: {
    verified: true,
    cpdAccredited: true,
    cityAndGuildsAssured: true,
    source: `https://www.healthandsafetygroup.com/healthcare/elearning/${slug}`,
    checkedOn: '2026-10-25',
    scope: 'online_theory_only',
  },
});

// These are training-provider course pages, NOT guaranteed equivalence to a
// particular commissioning contract or all levels within a composite requirement.
const OPTIONS = {
  safeguarding: {
    paid: course('cstf-safeguarding-adults-levels-1-and-2', 'Safeguarding Adults (CSTF Levels 1 & 2)'),
    note: 'Safeguarding adults/children and level requirements differ by role. Check your requirement before booking.',
  },
  safeguarding_children: {
    paid: course('cstf-safeguarding-children-levels-1-and-2', 'Safeguarding Children (CSTF Levels 1 & 2)'),
  },
  manual_handling: {
    paid: course('hsg-moving-handling-levels-1-2', 'People Moving & Handling (CSTF Levels 1 & 2)'),
    practical: { url: HSG_PRACTICAL_URL, title: 'Book practical skills training', provider: HSG },
    note: 'Online learning alone may not satisfy your practical moving and handling assessment.',
  },
  infection_control: {
    paid: course('cstf-infection-prevention-control', 'Infection Prevention & Control (CSTF)'),
    note: 'Book the full role-appropriate CSTF IPC course. Supplementary awareness modules must not be substituted for mandatory training.',
  },
  basic_life_support: {
    paid: course('hsg-resuscitation-adults-levels-1-2-3', 'Resuscitation Adults (CSTF Levels 1–3)'),
    practical: { url: HSG_PRACTICAL_URL, title: 'Book practical BLS training', provider: HSG },
    note: 'Do not rely on online theory as evidence of hands-on resuscitation competence.',
  },
  fire_safety: { paid: course('cstf-fire-safety', 'Fire Safety (CSTF)') },
  health_safety: { paid: course('cstf-health-safety-and-welfare', 'Health, Safety and Welfare (CSTF)') },
  information_governance: { paid: course('cstf-information-governance', 'Information Governance (CSTF)') },
  prevent: {
    paid: verifiedFreeCstf('cstf-preventing-radicalisation', 'Preventing Radicalisation (CSTF)'),
    note: 'Free HSG CSTF e-learning is CPD accredited and City & Guilds assured. Confirm appropriate Prevent level, completion evidence and employer acceptance. GOV.UK Prevent awareness is supplementary for healthcare.',
  },
  equality_diversity_human_rights: {
    paid: verifiedFreeCstf('cstf-equality-diversity-and-human-rights', 'Equality, Diversity and Human Rights (CSTF)'),
    note: 'Free HSG CSTF e-learning is CPD accredited and City & Guilds assured. Review the completion certificate for the worker’s role.',
  },
  learning_disability_autism_tier_2: {
    paid: {
      url: 'https://www.healthandsafetygroup.com/p/oliver-mcgowan-tier-2-1-day-mandatory-training',
      title: 'Oliver McGowan Tier 2 — one-day face-to-face course',
      provider: 'The Health & Safety Group', price: 'Check course and price',
    },
    note: 'Free Part 1 alone does NOT complete Tier 2. A live, approved one-day face-to-face Part 2 is also required when Tier 2 applies.',
  },
  care_certificate: {
    paid: { url: HSG_CATALOGUE_URL, title: 'Care Certificate training modules', provider: HSG, price: 'Varies' },
    note: 'A module or bundle alone does not complete the 2025 Care Certificate. The employer must assess all 16 standards and workplace competence.',
  },
};

const normalize = (value) => String(value || '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * Match stable requirement ID first, then human title. This is deliberately
 * conservative: unrelated topics fall back to the provider's full catalogue
 * instead of linking to a misleading course.
 */
export function getTrainingCourseOptions(item) {
  const values = typeof item === 'string'
    ? [item]
    : [item?.code, item?.training_code, item?.training_type, item?.name, item?.title, item?.id];
  const inputs = values.map(normalize).filter(Boolean);
  const is = (re) => inputs.some(value => re.test(value));
  let key = null;
  if (is(/oliver mcgowan|learning disability.*autism|autism.*tier 2|learning_disability_autism_tier_2/)) key = 'learning_disability_autism_tier_2';
  else if (is(/care certificate/)) key = 'care_certificate';
  else if (is(/equality.*diversity|human rights/)) key = 'equality_diversity_human_rights';
  else if (is(/equality|diversity|human rights/)) key = 'equality_diversity_human_rights';
  else if (is(/\bprevent\b|prevent awareness|preventing radicalisation/)) key = 'prevent';
  else if (is(/safeguarding.*children|child.*safeguarding/)) key = 'safeguarding_children';
  else if (is(/safeguarding/)) key = 'safeguarding';
  else if (is(/manual handling|moving.*handling|handling.*people|moving.*people/)) key = 'manual_handling';
  else if (is(/infection.*control|infection.*prevention|\bipc\b/)) key = 'infection_control';
  else if (is(/basic life support|\bbls\b|resuscitation/)) key = 'basic_life_support';
  else if (is(/fire safety|fire awareness/)) key = 'fire_safety';
  else if (is(/information governance|information security|data protection|\bgdpr\b/)) key = 'information_governance';
  else if (is(/health.*safety|safety.*health/)) key = 'health_safety';

  const option = OPTIONS[key];
  if (!option) return {
    key: 'catalogue', paid: { url: HSG_CATALOGUE_URL, title: 'Browse HSG healthcare courses', provider: HSG, price: 'Varies' },
    note: 'Choose a course matching your role and required training level. Ask your recruitment team if unsure.',
  };
  return { key, ...option };
}
